<template>
  <div class="chart-card" style="height: auto">
    <h3>Sermaye Artırımları</h3>
    <div class="sermaye-artirimlari-container">
      <div class="chart-container">
        <canvas ref="sermayeChart"></canvas>
      </div>
      <div class="table-container">
        <table class="sermaye-table">
          <thead>
            <tr>
              <th>Tarih</th>
              <th>Bedelli (%)</th>
              <th>Bedelsiz İK (%)</th>
              <th>Bedelsiz Temettü (%)</th>
              <th>Tahsisli (%)</th>
              <th>Sermaye (TL)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in sermayeArtirimlar" :key="index">
              <td data-label="Tarih">{{ formatTarih(item.Tarih) }}</td>
              <td data-label="Bedelli (%)">
                {{ item["Bedelli Oran (%)"] || "-" }}
              </td>
              <td data-label="Bedelsiz İK (%)">
                {{ item["Bedelsiz IK Oran (%)"] || "-" }}
              </td>
              <td data-label="Bedelsiz Temettü (%)">
                {{ item["Bedelsiz Temettü Oran (%)"] || "-" }}
              </td>
              <td data-label="Tahsisli (%)">
                {{ item["Tahsisli Oran (%)"] || "-" }}
              </td>
              <td data-label="Sermaye (TL)">
                {{
                  formatMoney(
                    parseFloat(
                      item["Bölünme Sonrası Sermaye (TL)"].replace(/\./g, "")
                    )
                  )
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";

export default {
  name: "SermayeArtirimlariGrafik",
  props: {
    sermayeArtirimlar: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      chart: null,
      chartColors: {
        bedelli: "rgba(239, 68, 68, 0.7)", // Kırmızı (Danger)
        bedelsizIK: "rgba(59, 130, 246, 0.7)", // Mavi (Primary Light)
        bedelsizTemettu: "rgba(16, 185, 129, 0.7)", // Yeşil (Success)
        tahsisli: "rgba(153, 102, 255, 0.7)", // Mor
        sermaye: "rgba(255, 159, 64, 0.7)", // Turuncu
      },
    };
  },
  computed: {
    sortedData() {
      return [...this.sermayeArtirimlar].sort((a, b) => {
        return (
          new Date(this.parseTarih(a.Tarih)) -
          new Date(this.parseTarih(b.Tarih))
        );
      });
    },
    minYil() {
      if (!this.sermayeArtirimlar.length) return "";
      return this.parseTarih(this.sortedData[0].Tarih).getFullYear();
    },
    maxYil() {
      if (!this.sermayeArtirimlar.length) return "";
      return this.parseTarih(
        this.sortedData[this.sortedData.length - 1].Tarih
      ).getFullYear();
    },
  },
  mounted() {
    this.renderChart();
  },
  methods: {
    parseTarih(tarih) {
      // GG-AA-YYYY formatını parse ediyoruz
      const [gun, ay, yil] = tarih.split("-").map(Number);
      return new Date(yil, ay - 1, gun);
    },
    formatTarih(tarih) {
      const date = this.parseTarih(tarih);
      return `${date.getDate().toString().padStart(2, "0")}.${(
        date.getMonth() + 1
      )
        .toString()
        .padStart(2, "0")}.${date.getFullYear()}`;
    },
    formatMoney(value) {
      return new Intl.NumberFormat("tr-TR").format(value) + " TL";
    },
    renderChart() {
      const ctx = this.$refs.sermayeChart.getContext("2d");

      // Tarihleri sıralı hale getir
      const sortedData = [...this.sermayeArtirimlar].sort((a, b) => {
        return (
          new Date(this.parseTarih(a.Tarih)) -
          new Date(this.parseTarih(b.Tarih))
        );
      });

      const labels = sortedData.map((item) => this.formatTarih(item.Tarih));

      const bedelliData = sortedData.map((item) =>
        item["Bedelli Oran (%)"]
          ? parseFloat(item["Bedelli Oran (%)"].replace(",", "."))
          : 0
      );
      const bedelsizIKData = sortedData.map((item) =>
        item["Bedelsiz IK Oran (%)"]
          ? parseFloat(item["Bedelsiz IK Oran (%)"].replace(",", "."))
          : 0
      );
      const bedelsizTemettuData = sortedData.map((item) =>
        item["Bedelsiz Temettü Oran (%)"]
          ? parseFloat(item["Bedelsiz Temettü Oran (%)"].replace(",", "."))
          : 0
      );
      const tahsisliData = sortedData.map((item) =>
        item["Tahsisli Oran (%)"]
          ? parseFloat(item["Tahsisli Oran (%)"].replace(",", "."))
          : 0
      );

      // Sermaye miktarlarını milyon TL olarak normalize ediyoruz
      const sermayeData = sortedData.map((item) =>
        parseFloat(
          item["Bölünme Sonrası Sermaye (TL)"].replace(/\./g, "") / 1000000
        )
      );

      this.chart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: labels,
          datasets: [
            {
              label: "Bedelli (%)",
              data: bedelliData,
              backgroundColor: this.chartColors.bedelli,
              borderColor: this.chartColors.bedelli,
              borderWidth: 1,
            },
            {
              label: "Bedelsiz İK (%)",
              data: bedelsizIKData,
              backgroundColor: this.chartColors.bedelsizIK,
              borderColor: this.chartColors.bedelsizIK,
              borderWidth: 1,
            },
            {
              label: "Bedelsiz Temettü (%)",
              data: bedelsizTemettuData,
              backgroundColor: this.chartColors.bedelsizTemettu,
              borderColor: this.chartColors.bedelsizTemettu,
              borderWidth: 1,
            },
            {
              label: "Tahsisli (%)",
              data: tahsisliData,
              backgroundColor: this.chartColors.tahsisli,
              borderColor: this.chartColors.tahsisli,
              borderWidth: 1,
            },
            {
              label: "Sermaye (Milyon TL)",
              data: sermayeData,
              type: "line",
              backgroundColor: this.chartColors.sermaye,
              borderColor: this.chartColors.sermaye,
              borderWidth: 2,
              fill: false,
              pointRadius: 5,
              yAxisID: "y1",
              order: -1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              title: {
                display: true,
                text: "Tarih",
                color: "#f8fafc",
              },
              ticks: {
                color: "#f8fafc",
              },
              grid: {
                color: "#444",
              },
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: "Artırım Oranı (%)",
                color: "#f8fafc",
              },
              ticks: {
                color: "#f8fafc",
              },
              grid: {
                color: "#444",
              },
              position: "left",
            },
            y1: {
              beginAtZero: true,
              position: "right",
              title: {
                display: true,
                text: "Sermaye (Milyon TL)",
                color: "#f8fafc",
              },
              ticks: {
                color: "#f8fafc",
              },
              grid: {
                drawOnChartArea: false,
                color: "#444",
              },
            },
          },
          plugins: {
            tooltip: {
              callbacks: {
                label: function (context) {
                  const datasetLabel = context.dataset.label;
                  const value = context.raw;

                  if (datasetLabel === "Sermaye (Milyon TL)") {
                    return `${datasetLabel}: ${value.toFixed(2)} Milyon TL`;
                  }
                  return `${datasetLabel}: %${value}`;
                },
              },
            },
            legend: {
              position: "top",
              labels: {
                color: "#f8fafc",
              },
            },
          },
        },
      });
    },
  },
  watch: {
    sermayeArtirimlar: {
      handler() {
        if (this.chart) {
          this.chart.destroy();
        }
        this.$nextTick(() => {
          this.renderChart();
        });
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.sermaye-artirimlari-container {
  padding: 20px;
  max-width: 1200px;
  color: #f8fafc;
  font-size: 12px;
}

h2 {
  color: #f8fafc;
  margin-bottom: 20px;
  font-size: 12px;
}

.chart-container {
  position: relative;
  height: 400px;
  margin-bottom: 30px;
}

.table-container {
  margin-top: 30px;
  overflow-x: auto;
}

.sermaye-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  color: #f8fafc;
  font-size: 12px;
}

.sermaye-table th,
.sermaye-table td {
  border: 1px solid var(--border-color);
  padding: 8px;
  text-align: center;
  font-size: 12px;
  color: var(--text-secondary);
}

.sermaye-table th {
  background-color: var(--card-dark);
  font-weight: bold;
  color: #f8fafc;
}

.sermaye-table tr:nth-child(even) {
  background-color: rgba(255, 255, 255, 0.05);
}

.sermaye-table tr:nth-child(odd) {
  background-color: rgba(255, 255, 255, 0.02);
}

.sermaye-table tr:hover {
  background-color: rgba(55, 175, 255, 0.1);
}

@media (max-width: 768px) {
  .sermaye-artirimlari-container {
    padding: 10px;
    font-size: 11px;
  }

  .chart-container {
    height: 400px;
    margin-bottom: 20px;
  }

  .sermaye-table {
    font-size: 11px;
  }

  .sermaye-table thead {
    display: none;
  }

  .sermaye-table,
  .sermaye-table tbody,
  .sermaye-table tr {
    display: block;
    width: 100%;
  }

  .sermaye-table tr {
    margin-bottom: 15px;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    overflow: hidden;
  }

  .sermaye-table td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-align: right;
    padding: 10px 8px;
    border: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    color: var(--text-secondary);
  }

  .sermaye-table td:last-child {
    border-bottom: none;
  }

  .sermaye-table td::before {
    content: attr(data-label);
    font-weight: bold;
    text-align: left;
    padding-right: 10px;
    color: var(--text-secondary);
  }

  .sermaye-table tr:nth-child(even) {
    background-color: rgba(255, 255, 255, 0.05);
  }

  .sermaye-table tr:nth-child(odd) {
    background-color: rgba(255, 255, 255, 0.02);
  }
}

@media (max-width: 480px) {
  .sermaye-artirimlari-container {
    padding: 4px;
    font-size: 10px;
  }

  h2 {
    font-size: 11px;
    text-align: center;
  }

  .chart-container {
    height: 400px;
  }

  .sermaye-table td {
    padding: 4px 2px;
    font-size: 10px;
  }
}
</style>
